// DevTerminal.js — Terminal xterm + WebSocket (+ coller clipboard)

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { ClipboardPaste } from 'lucide-react';
import '@xterm/xterm/css/xterm.css';
import { getTerminalWsUrl } from '../../services/devCockpitApi';
import './devCockpit.css';

function sendToPty(ws, text) {
    if (!text || !ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(text);
    return true;
}

export default function DevTerminal({ workspaceReady, onError }) {
    const containerRef = useRef(null);
    const termRef = useRef(null);
    const fitRef = useRef(null);
    const wsRef = useRef(null);
    const [pasteHint, setPasteHint] = useState(null);

    const pasteFromClipboard = useCallback(async () => {
        const ws = wsRef.current;
        const term = termRef.current;
        try {
            const text = await navigator.clipboard.readText();
            if (!text) {
                setPasteHint('Presse-papiers vide');
                return;
            }
            if (!sendToPty(ws, text)) {
                setPasteHint('Terminal non connecté');
                return;
            }
            term?.focus();
            setPasteHint(null);
        } catch {
            setPasteHint('Autorise le presse-papiers (ou Ctrl+Shift+V)');
        }
    }, []);

    const connect = useCallback(() => {
        if (!workspaceReady || !containerRef.current) return;

        const root = containerRef.current.closest('.dev-workspace');
        const styles = root ? getComputedStyle(root) : null;
        const bg = styles?.getPropertyValue('--ws-code').trim() || '#1a1a28';
        const fg = styles?.getPropertyValue('--ws-text').trim() || '#e8e8f0';
        const accent = styles?.getPropertyValue('--ws-accent-icon').trim() || '#a5b4fc';

        const term = new Terminal({
            cursorBlink: true,
            fontSize: 11,
            fontFamily: "'JetBrains Mono', Menlo, Monaco, Consolas, monospace",
            theme: {
                background: bg,
                foreground: fg,
                cursor: accent,
                selectionBackground: 'rgba(165, 180, 252, 0.22)',
            },
            rows: 10,
            rightClickSelectsWord: false,
        });
        const fit = new FitAddon();
        term.loadAddon(fit);
        term.open(containerRef.current);
        fit.fit();

        termRef.current = term;
        fitRef.current = fit;

        term.writeln('\x1b[38;2;165;180;252m▸ sandbox isolé\x1b[0m');
        term.writeln('Connexion…\r\n');

        let ws;
        try {
            ws = new WebSocket(getTerminalWsUrl());
        } catch (err) {
            term.writeln(`\x1b[31mErreur WS: ${err.message}\x1b[0m`);
            onError?.(err.message);
            return;
        }
        wsRef.current = ws;

        ws.onopen = () => {
            term.writeln('\x1b[32mConnecté.\x1b[0m\r\n');
        };

        ws.onmessage = (ev) => {
            term.write(ev.data);
        };

        ws.onerror = () => {
            term.writeln('\r\n\x1b[31mErreur connexion terminal.\x1b[0m');
            onError?.('Erreur WebSocket terminal');
        };

        ws.onclose = () => {
            term.writeln('\r\n\x1b[33mSession fermée.\x1b[0m');
        };

        term.onData((data) => {
            if (ws.readyState === WebSocket.OPEN) {
                ws.send(data);
            }
        });

        // Ctrl/Cmd+V → coller (évite d'envoyer \x16 au PTY)
        // Ctrl/Cmd+C avec sélection → copier (sans SIGINT)
        term.attachCustomKeyEventHandler((ev) => {
            if (ev.type !== 'keydown') return true;
            const mod = ev.ctrlKey || ev.metaKey;
            if (!mod || ev.altKey) return true;

            const key = ev.key.toLowerCase();
            if (key === 'v' && !ev.shiftKey) {
                ev.preventDefault();
                navigator.clipboard.readText().then((text) => {
                    sendToPty(ws, text);
                }).catch(() => { /* bouton Coller en fallback */ });
                return false;
            }
            if (key === 'c' && !ev.shiftKey && term.hasSelection()) {
                ev.preventDefault();
                const sel = term.getSelection();
                if (sel) navigator.clipboard.writeText(sel).catch(() => {});
                return false;
            }
            return true;
        });

        const onPaste = (ev) => {
            const text = ev.clipboardData?.getData('text/plain');
            if (!text) return;
            ev.preventDefault();
            ev.stopPropagation();
            sendToPty(ws, text);
        };

        const onContextMenu = (ev) => {
            ev.preventDefault();
            navigator.clipboard.readText().then((text) => {
                sendToPty(ws, text);
            }).catch(() => {});
        };

        const viewport = containerRef.current;
        viewport?.addEventListener('paste', onPaste, true);
        viewport?.addEventListener('contextmenu', onContextMenu);

        const onResize = () => {
            try { fit.fit(); } catch { /* ignore */ }
            if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
            }
        };
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
            viewport?.removeEventListener('paste', onPaste, true);
            viewport?.removeEventListener('contextmenu', onContextMenu);
            ws.close();
            term.dispose();
        };
    }, [workspaceReady, onError]);

    useEffect(() => {
        const cleanup = connect();
        return () => {
            cleanup?.();
            wsRef.current?.close();
            termRef.current?.dispose();
        };
    }, [connect]);

    return (
        <div className="dev-terminal">
            <div className="dev-frame__panel-header">
                <span>Terminal</span>
                <div className="dev-terminal__header-actions">
                    {pasteHint && <span className="dev-terminal__paste-hint">{pasteHint}</span>}
                    <button
                        type="button"
                        className="dev-btn dev-btn--icon"
                        title="Coller (Ctrl+V) — clic droit aussi"
                        onClick={pasteFromClipboard}
                        disabled={!workspaceReady}
                    >
                        <ClipboardPaste size={12} />
                        Coller
                    </button>
                    <span className="dev-frame__panel-header-meta">sandbox HITL</span>
                </div>
            </div>
            {workspaceReady ? (
                <div ref={containerRef} className="dev-terminal__viewport" />
            ) : (
                <div className="dev-terminal__placeholder">
                    Disponible quand le workspace est prêt
                </div>
            )}
        </div>
    );
}
